import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, FileText, CheckCircle2 } from "lucide-react";
import api, { API, formatApiError } from "../lib/api";

export default function Forms() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadForm, setUploadForm] = useState({ full_name: "", email: "", phone: "", form_type: "Signed Pickup Form", note: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedOk, setSubmittedOk] = useState(false);

  useEffect(() => {
    api.get("/form-templates").then((r) => setTemplates(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setUploadForm({ ...uploadForm, [k]: v });

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach a file.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("full_name", uploadForm.full_name);
      fd.append("email", uploadForm.email);
      fd.append("phone", uploadForm.phone);
      fd.append("form_type", uploadForm.form_type);
      fd.append("note", uploadForm.note);
      fd.append("file", file);
      await api.post("/submitted-forms", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmittedOk(true);
      toast.success("Form uploaded successfully!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full px-4 py-3 bg-white border border-slate-200 rounded-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/20 text-sm";
  const label = "block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2";

  return (
    <div data-testid="forms-page">
      <section className="bg-[#1E2A78] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700] mb-3">Forms</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">Download forms. Upload signed copies.</h1>
          <p className="mt-4 text-slate-200 max-w-2xl">Grab the right form for your shipment, fill it out, then upload your signed copy below.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-display font-bold text-[#0F172A] flex items-center gap-2"><Download className="h-5 w-5 text-[#1E2A78]" /> Available Forms</h2>
            <div className="mt-6 space-y-3">
              {loading ? (
                <div className="text-slate-500 text-sm">Loading…</div>
              ) : templates.length === 0 ? (
                <div className="p-6 bg-[#F1F5F9] border border-slate-200 rounded-sm text-sm text-slate-600" data-testid="forms-empty">
                  No downloadable forms yet. Please contact our team — we'll email the forms you need directly.
                </div>
              ) : (
                templates.map((t) => (
                  <a
                    key={t.id}
                    href={`${API}/form-templates/${t.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-5 border border-slate-200 rounded-sm hover:border-[#1E2A78] hover:bg-slate-50 transition-colors group"
                    data-testid={`form-template-${t.id}`}
                  >
                    <div className="h-10 w-10 bg-[#FFD700]/20 border border-[#FFD700] rounded-sm flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-[#1E2A78]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-[#0F172A]">{t.title}</div>
                      {t.description && <div className="text-sm text-slate-600 mt-1">{t.description}</div>}
                      <div className="text-xs text-slate-400 mt-1 font-mono">{t.filename}</div>
                    </div>
                    <Download className="h-5 w-5 text-slate-400 group-hover:text-[#1E2A78] flex-shrink-0" />
                  </a>
                ))
              )}
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-display font-bold text-[#0F172A] flex items-center gap-2"><Upload className="h-5 w-5 text-[#1E2A78]" /> Upload Signed Form</h2>
            {submittedOk ? (
              <div className="mt-6 p-8 bg-white border border-slate-200 rounded-sm text-center" data-testid="form-upload-success">
                <CheckCircle2 className="h-12 w-12 text-[#1E2A78] mx-auto mb-3" />
                <div className="font-display font-bold text-lg">Form received!</div>
                <p className="text-slate-600 text-sm mt-2">Our team will process it and reach out if anything's needed.</p>
                <button onClick={() => { setSubmittedOk(false); setFile(null); setUploadForm({ ...uploadForm, note: "" }); }} className="mt-5 px-5 py-2 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200]">
                  Upload another
                </button>
              </div>
            ) : (
              <form onSubmit={upload} className="mt-6 space-y-4 bg-white border border-slate-200 p-6 rounded-sm" data-testid="form-upload-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className={label}>Full Name</label>
                    <input required value={uploadForm.full_name} onChange={(e) => set("full_name", e.target.value)} className={input} data-testid="upload-name" />
                  </div>
                  <div>
                    <label className={label}>Phone</label>
                    <input value={uploadForm.phone} onChange={(e) => set("phone", e.target.value)} className={input} data-testid="upload-phone" />
                  </div>
                </div>
                <div>
                  <label className={label}>Email</label>
                  <input required type="email" value={uploadForm.email} onChange={(e) => set("email", e.target.value)} className={input} data-testid="upload-email" />
                </div>
                <div>
                  <label className={label}>Form Type</label>
                  <select value={uploadForm.form_type} onChange={(e) => set("form_type", e.target.value)} className={input} data-testid="upload-type">
                    <option>Signed Pickup Form</option>
                    <option>Shipper Declaration</option>
                    <option>ID / Proof of Address</option>
                    <option>Receiver Authorization</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Notes (optional)</label>
                  <textarea rows="3" value={uploadForm.note} onChange={(e) => set("note", e.target.value)} className={input} data-testid="upload-note" />
                </div>
                <div>
                  <label className={label}>Attach File (PDF / Image)</label>
                  <input
                    type="file"
                    required
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#1E2A78] file:text-white file:font-semibold hover:file:bg-[#17205E]"
                    data-testid="upload-file"
                  />
                </div>
                <button type="submit" disabled={submitting} className="w-full px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] disabled:opacity-60" data-testid="upload-submit">
                  {submitting ? "Uploading..." : "Upload Form"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
